import React from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { Checkbox } from '../../components/ui/checkbox';
import { Users, AlertTriangle, Wallet, Trash2 } from 'lucide-react';

// Alert for existing payments when changing members
export const MembersPaymentAlert = ({
  open,
  onOpenChange,
  existingPayments,
  formatCurrency,
  onCancel,
  onConfirm,
}) => {
  const totalAmount = existingPayments?.reduce((sum, p) => sum + (p.total_amount || 0), 0) || 0;

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-yellow-500" />
            Pagamentos existentes
          </AlertDialogTitle>
          <AlertDialogDescription>
            <span className="block text-yellow-200 bg-yellow-500/20 border border-yellow-500/50 rounded-lg p-3 mt-2">
              <strong>Atenção:</strong> Esta atividade possui{' '}
              <span className="font-semibold">{existingPayments?.length || 0} pagamento(s)</span> já rateados entre os membros, no valor total de{' '}
              <span className="text-white font-semibold">{formatCurrency(totalAmount)}</span>.
            </span>
            <span className="block text-sm text-slate-400 mt-3">
              Ao alterar os membros, todos os pagamentos vinculados serão <span className="text-red-400 font-semibold">excluídos</span> e você precisará <span className="text-orange-400 font-semibold">refazer o rateio</span>.
            </span>
            <span className="block text-xs text-slate-500 mt-2">
              Deseja continuar?
            </span>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter className="flex-col sm:flex-row gap-2">
          <AlertDialogCancel onClick={onCancel} className="w-full sm:w-auto">
            Cancelar
          </AlertDialogCancel>
          <AlertDialogAction 
            onClick={onConfirm}
            className="w-full sm:w-auto bg-orange-500 hover:bg-orange-600"
          >
            <Wallet className="w-4 h-4 mr-2" />
            Excluir e refazer rateio
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

// Alert asking if user wants to add members
export const MembersAlertDialog = ({
  open,
  onOpenChange,
  onAddNow,
  onAddLater,
}) => {
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <Users className="w-5 h-5 text-orange-500" />
            Adicionar membros?
          </AlertDialogTitle>
          <AlertDialogDescription>
            Deseja já adicionar os membros a esta atividade?
            <span className="block text-xs text-slate-500 mt-2">
              Lembrando que esta ação poderá ser feita depois.
            </span>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter className="flex-col sm:flex-row gap-2">
          <AlertDialogCancel onClick={onAddLater} className="w-full sm:w-auto">
            Adicionar mais tarde
          </AlertDialogCancel>
          <AlertDialogAction 
            onClick={onAddNow}
            className="w-full sm:w-auto bg-orange-500 hover:bg-orange-600"
          >
            <Users className="w-4 h-4 mr-2" />
            Adicionar agora
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

// Dialog for selecting members
export const MembersSelectionDialog = ({
  open,
  onOpenChange,
  activityTitle,
  members,
  selectedMembers,
  selectAllMembers,
  onToggleAllMembers,
  onToggleMemberSelection,
  onSave,
  onCancel,
}) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users className="w-5 h-5 text-orange-500" />
            Adicionar membros
          </DialogTitle>
          <DialogDescription>
            {activityTitle && (
              <>Atividade: <span className="text-white font-medium">&quot;{activityTitle}&quot;</span></>
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Select All */}
          <div className="flex items-center justify-between bg-slate-800 rounded-lg p-3">
            <div className="flex items-center gap-2">
              <Users className="w-4 h-4 text-orange-400" />
              <Label className="text-sm">Todos os membros</Label>
            </div>
            <Checkbox
              checked={selectAllMembers}
              onCheckedChange={onToggleAllMembers}
              data-testid="select-all-members"
            />
          </div>
          
          {/* Members List */}
          <div className="bg-slate-800/50 rounded-lg p-3 space-y-2 max-h-60 overflow-y-auto">
            {members?.map((member) => (
              <label
                key={member.user_id}
                className="flex items-center gap-3 p-2 rounded hover:bg-slate-700 cursor-pointer"
              >
                <Checkbox
                  checked={selectAllMembers || selectedMembers?.includes(member.user_id)}
                  onCheckedChange={() => onToggleMemberSelection(member.user_id)}
                />
                <span className="text-sm text-slate-200">{member.user_name}</span>
              </label>
            ))}
          </div>
          
          <p className="text-xs text-slate-500 text-center">
            {selectAllMembers 
              ? 'Todos os membros da viagem participarão' 
              : `${selectedMembers?.length || 0} membro(s) selecionado(s)`}
          </p>
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button 
            variant="outline" 
            onClick={onCancel}
            className="w-full sm:w-auto"
          >
            Cancelar
          </Button>
          <Button 
            onClick={onSave}
            className="w-full sm:w-auto bg-orange-500 hover:bg-orange-600"
            disabled={!selectAllMembers && (selectedMembers?.length || 0) === 0}
          >
            Salvar membros
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Dialog for viewing/editing existing activity members
export const ActivityMembersDialog = ({
  open,
  onOpenChange,
  activity,
  members,
  selectedMembers,
  selectAllMembers,
  onToggleAllMembers,
  onToggleMemberSelection,
  onSave,
  onCancel,
}) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users className="w-5 h-5 text-orange-500" />
            Membros da Atividade
          </DialogTitle>
          <DialogDescription>
            {activity?.title && (
              <>Atividade: <span className="text-white font-medium">&quot;{activity.title}&quot;</span></>
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Select All */}
          <div className="flex items-center justify-between bg-slate-800 rounded-lg p-3">
            <div className="flex items-center gap-2">
              <Users className="w-4 h-4 text-orange-400" />
              <Label className="text-sm">Todos os membros</Label>
            </div>
            <Checkbox
              checked={selectAllMembers}
              onCheckedChange={onToggleAllMembers}
              data-testid="select-all-activity-members"
            />
          </div>
          
          {/* Members List */}
          <div className="bg-slate-800/50 rounded-lg p-3 space-y-2 max-h-60 overflow-y-auto">
            {members?.map((member) => (
              <label
                key={member.user_id}
                className="flex items-center gap-3 p-2 rounded hover:bg-slate-700 cursor-pointer"
              >
                <Checkbox
                  checked={selectAllMembers || selectedMembers?.includes(member.user_id)}
                  onCheckedChange={() => onToggleMemberSelection(member.user_id)}
                />
                <span className="text-sm text-slate-200">{member.user_name}</span>
              </label>
            ))}
          </div>
          
          <p className="text-xs text-slate-500 text-center">
            {selectAllMembers 
              ? 'Todos os membros da viagem participarão' 
              : `${selectedMembers?.length || 0} membro(s) selecionado(s)`}
          </p>
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button 
            variant="outline" 
            onClick={onCancel}
            className="w-full sm:w-auto"
          >
            Cancelar
          </Button>
          <Button 
            onClick={onSave}
            className="w-full sm:w-auto bg-orange-500 hover:bg-orange-600"
            disabled={!selectAllMembers && (selectedMembers?.length || 0) === 0}
          >
            Salvar membros
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
